import React, { useState, useRef } from 'react';
import { pdf } from '@react-pdf/renderer';
import SuiviHeader from '../../composants/SuiviHeader/SuiviHeader';
import TabNav from '../../composants/TabNav/TabNav';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import SuiviCommandePDF from '../../pages/PDF/SuiviCommandePDF';
import SuiviEntityContent from '../../composants/SuiviEntityContent/SuiviEntityContent';
import { useSuiviEntity } from '../../hooks/useSuiviEntity';
import { useSuiviEntityTabs } from '../../hooks/useSuiviEntityTabs';
import { useSuiviEntityActions } from '../../hooks/useSuiviEntityActions';
import { commandeBCService } from '../../services/entityService';
import { getStatusColor, formatApiDate } from '../../utils/entityUtils';
import './SuiviDeCommandeBC.css';
import { getLotImage } from '../../utils/getLotImage';

const SuiviDeCommandeBC: React.FC = () => {
  const [errors, setErrors] = useState<any>({});
  // NOUVEAU: État pour les CGV
  const [acceptCGV, setAcceptCGV] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);


  // CORRECTION: Utiliser useSuiviEntity avec le service BC dédié
  const {
    entity: commande,
    setEntity: setCommande,
    selectedPrestataire,
    setSelectedPrestataire,
    techNotes,
    setTechNotes,
    loading,
    error
  } = useSuiviEntity({
    service: commandeBCService,
    entitiesKey: 'commandes'
  });

  // CORRECTION: Utiliser useSuiviEntityTabs pour les prestataires BC
  const {
    activeTab,
    tabs,
    commandeType,
    isClientView,
    isPrestataireView,
    getDisabledTabs,
    getInactiveTabs,
    handleTabChange
  } = useSuiviEntityTabs(commande, {
    entityType: 'commande',
    getCommandeType: () => 'BC' // Force toujours BC pour les commandes BC
  });

  // CORRECTION: Utiliser useSuiviEntityActions pour les prestataires BC
  const {
    isRefuseModalOpen,
    setIsRefuseModalOpen,
    isSupprimerModalOpen,
    setIsSupprimerModalOpen,
    handleRefuseEntity,
    handleDeleteEntity,
    handleFinaliserEntity,
    handleLancerConsultation
  } = useSuiviEntityActions({
    service: commandeBCService,
    entityType: 'commande',
    redirectRoute: '/commandes'
  });

  // CORRECTION: Simplifier la validation avec la logique standard
  const validateConsultationForm = () => {
    const newErrors: any = {};

    // CORRECTION: Adapter aux données API reçues pour les prestataires BC
    const operationData = commande?.operation;

    if (!operationData?.nom?.trim()) {
      newErrors.nomOperation = "Le nom de l'opération est obligatoire";
    }
    if (!operationData?.besoin?.trim()) {
      newErrors.besoin = "Le cahier des charges est obligatoire";
    }
    if (!operationData?.adresse?.trim()) {
      newErrors.adresse = "L'adresse est obligatoire";
    }
    if (!operationData?.ville?.trim()) {
      newErrors.ville = "La ville est obligatoire";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // CORRECTION: Export PDF adapté aux données API prestataires BC
  const handleExport = async () => {
    if (!commande) return;

    // CORRECTION: Utiliser la structure des données API prestataires BC
    const pdfData = {
      ...commande,
      id: commande.id.toString(),
      numeroOperation: commande.operation?.numero || `BC-${commande.id}`,
      titre: commande.operation?.nom || 'Commande BC',
      lotTechnique: `${commande.operation?.lot_technique?.id} - ${commande.operation?.lot_technique?.nom}`,
      lotGeographique: `${commande.operation?.lot_departement?.departement?.id} - ${commande.operation?.lot_departement?.departement?.nom}`,
      status: getStatusName(commande.id_status),
      type: 'BC',
      dateCreation: formatApiDate(commande.created_at),
      dateValidation: formatApiDate(commande.updated_at),
      prestataire: {
        nom: commande.contrat?.entite?.nom || '',
        contact: `${commande.contrat?.entite?.interlocuteur?.prenom || ''} ${commande.contrat?.entite?.interlocuteur?.nom || ''}`.trim(),
        email: commande.contrat?.entite?.interlocuteur?.email_contact || '',
        telephone: commande.contrat?.entite?.interlocuteur?.telephone || ''
      },
      client: {
        organisme: commande.operation?.organisme?.nom || '',
        contact: `${commande.operation?.acheteur?.prenom || ''} ${commande.operation?.acheteur?.nom || ''}`.trim(),
        email: commande.operation?.acheteur?.email_contact || '',
        telephone: commande.operation?.acheteur?.telephone || ''
      }
    };

    const blob = await pdf(<SuiviCommandePDF commande={pdfData} />).toBlob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commande-bc-${commande.operation?.numero || commande.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // SUPPRIMÉ: Toutes les fonctions getStatusName et mapping complexe

  // CORRECTION: Nouvelle fonction pour adapter le statut selon le rôle
  const getDisplayStatus = () => {
    if (!commande?.status) return 'Statut inconnu';

    const statusName = commande.status.nom;
    const statusId = commande.status.id;

    // Pour les prestataires BC, adapter l'affichage selon le contexte
    if (isPrestataireView) {
      const rang = getRangPrestataire();

      // Si c'est "En attente de l'acceptation du rang X" et que c'est le rang du prestataire connecté
      if (statusName === `En attente de l'acceptation du rang ${rang}`) {
        return 'À accepter';
      }
      // Sinon, utiliser le nom tel quel
      return statusName;
    }

    // Pour les acheteurs, utiliser le nom tel quel
    return statusName;
  };

  // CORRECTION: Déterminer le rang du prestataire depuis les données API
  const getRangPrestataire = () => {
    if (!commande || !isPrestataireView) return 1;
    return commande.rang || 1;
  };

  // CORRECTION: Fonction pour formater le sous-titre
  const getSousTitre = () => {
    const numero = commande.operation?.numero || commande.id;
    const rang = getRangPrestataire();

    if (isPrestataireView) {
      return `Suivi de la Commande BC n° ${numero} - Rang ${rang}`;
    }
    return `Suivi de la Commande BC n° ${numero}`;
  };

  // CORRECTION: Fonction pour formater le subtitle (lot technique + géographique)
  const getSubtitle = () => {
    const lotTechnique = commande.operation?.lot_technique;
    const departement = commande.operation?.lot_departement?.departement;

    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  if (loading) {
    return <div className="loading-indicator loading-fullpage">Chargement des détails de la commande BC...</div>;
  }

  if (error && !commande) {
    return (
      <div className="error-message">
        <p>Erreur: {error}</p>
        <p>Impossible de charger les détails de cette commande BC.</p>
      </div>
    );
  }

  if (!commande) {
    return <div className="empty-state">Aucune commande BC trouvée avec cet identifiant.</div>;
  }

  console.log('=== DEBUG COMMANDE BC PRESTATAIRE ===');
  console.log('Commande complète:', commande);
  console.log('Articles:', commande.articles);
  console.log('Operation:', commande.operation);
  console.log('Status:', commande.status);
  console.log('Transferred:', commande.transferred);

  return (
    <div className="suivi-commande" ref={contentRef}>
      <div className="no-print">
        <SuiviHeader
          titre={commande.operation?.nom || 'Commande BC'}
          sousTitre={getSousTitre()}
          subtitle={getSubtitle()}
          // CORRECTION: Utiliser la nouvelle fonction simplifiée
          status={getDisplayStatus()}
          statusColor={getStatusColor(getDisplayStatus())}
          delais={commande.expire_at}
          backgroundImage={getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom)}
          source="BC"
          statusId={commande.status?.id}
        />
      </div>

      <div className="suivi-de-commande-content">
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          disabledTabs={getDisabledTabs()}
          inactiveTabs={getInactiveTabs()}
        />

        <div className="tab-content">
          <SuiviEntityContent
            entity={commande}
            setEntity={setCommande}
            activeTab={activeTab}
            commandeType={commandeType}
            isClientView={isClientView}
            isPrestataireView={isPrestataireView}
            selectedPrestataire={selectedPrestataire}
            setSelectedPrestataire={setSelectedPrestataire}
            techNotes={techNotes}
            setTechNotes={setTechNotes}
            errors={errors}
            setErrors={setErrors}
            acceptCGV={acceptCGV} // NOUVEAU: Passer acceptCGV
            setAcceptCGV={setAcceptCGV} // NOUVEAU: Passer setAcceptCGV
            handleExport={handleExport}
            validateConsultationForm={validateConsultationForm}
            handleLancerConsultation={handleLancerConsultation}
            handleFinaliserCommande={handleFinaliserEntity}
            entityType="commande"
            printRef={contentRef}
          />
        </div>

        {/* Modals */}
        <ValidationPopUp
          isOpen={isRefuseModalOpen}
          title={"Refus de la commande"}
          text="Êtes-vous sûr de vouloir refuser cette commande ?"
          onClose={() => setIsRefuseModalOpen(false)}
          onConfirm={async () => {
            handleRefuseEntity();
            setIsRefuseModalOpen(false);
          }}
          cancelLabel="Non"
          confirmLabel="Oui"
        />

        <ValidationPopUp
          isOpen={isSupprimerModalOpen}
          title={"Suppression de la commande"}
          text="Êtes-vous sûr de vouloir supprimer cette commande ?"
          onClose={() => setIsSupprimerModalOpen(false)}
          onConfirm={() => {
            handleDeleteEntity();
            setIsSupprimerModalOpen(false);
          }}
          cancelLabel="Non"
          confirmLabel="Oui"
        />
      </div>
    </div>
  );
};

export default SuiviDeCommandeBC;